import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useTable, useFilters, usePagination } from "react-table";
import { Badge, ButtonGroup } from "react-bootstrap";

import { ResidenteService } from "services/ResidenteService";
import { getDefaultParams } from "services/DefaultParamets";
import Tables from "jsx/components/CitoPlus/components/tables";
import SearchInput from "jsx/components/CitoPlus/components/input_search";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import Comentarios from "jsx/components/CitoPlus/components/sidebar_comments";
import {
  CitoPlusLinkIconPrimary,
  LinkPrimary,
} from "jsx/components/CitoPlus/components/CitoPlusLink";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";

import "../../../table/FilteringTable/filtering.css";

const URL_NOTAS = "/api/v3/residentes/autorizaciones-notas/";

const AutorizacionesList = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");

  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);

  const [defaultParams] = useState(getDefaultParams);

  // Comentarios
  const [showSidebarComments, setShowSidebarComments] = useState(false);
  const [urlComentarios] = useState(URL_NOTAS);
  const [urlComentariosParams, setUrlComentariosParams] = useState({});
  const [selectedPrePostData, setSelectedPrePostData] = useState({});

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 0:
        return <Badge bg="success">Aprobado</Badge>;
      case 1:
        return <Badge bg="danger">Rechazado</Badge>;
      case 2:
        return <Badge bg="warning">En Proceso</Badge>;
      case 4:
        return <Badge bg="info">Habilitado</Badge>;
      case 5:
        return <Badge bg="secondary">Deshabilitado</Badge>;
      case 6:
        return <Badge bg="primary">Abierto</Badge>;
      case 7:
        return <Badge bg="dark">Cerrado</Badge>;
      default:
        return <Badge bg="secondary">Desconocido</Badge>;
    }
  };

  const handleComentariosClick = (slug) => {
    setSelectedPrePostData({
      autorizacion: slug,
      nota: "",
      foto: null,
    });
    setUrlComentariosParams({
      autorizacion: slug,
      page_size: 100,
    });
    setShowSidebarComments(true);
  };

  const handleNewComment = () => {
    fetchUrl(currentPage, searchValue);
  };

  const columns = useMemo(
    () => [
      {
        id: "created_at",
        Header: "Fecha Creación",
        accessor: "created_at",
        disableFilters: true,
        disableSortable: false,
      },
      {
        id: "consecutivo",
        Header: "#",
        accessor: "consecutivo",
        disableFilters: true,
        Cell: ({ value }) => value || "-",
      },
      {
        id: "asunto",
        Header: "Asunto",
        accessor: "asunto",
        disableFilters: true,
        Cell: ({ value }) => {
          if (!value) return "-";
          return value.length > 40 ? `${value.substring(0, 40)}...` : value;
        },
      },
      {
        id: "nota",
        Header: "Descripción",
        accessor: "nota",
        disableFilters: true,
        Cell: ({ value }) => {
          if (!value) return "-";
          return value.length > 50 ? `${value.substring(0, 50)}...` : value;
        },
      },
      {
        id: "fecha_limite",
        Header: "Fecha Límite",
        accessor: "fecha_limite",
        disableFilters: true,
        disableSortable: false,
        Cell: ({ value }) => value || "-",
      },
      {
        id: "estado",
        Header: "Estado",
        accessor: "estado",
        disableFilters: true,
        disableSortable: true,
        Cell: ({ value }) => getEstadoBadge(value),
      },
      {
        id: "actions",
        Header: "",
        disableFilters: true,
        disableSortable: true,
        accessor: (row) => {
          return (
            <ButtonGroup>
              <CitoPlusLinkIconPrimary
                to={`/autorizacion/${row.slug}`}
                title="Ver detalles"
              >
                <i className="fas fa-eye"></i>
              </CitoPlusLinkIconPrimary>
              <CitoPlusLinkIconPrimary
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleComentariosClick(row.slug);
                }}
                className="position-relative"
                title="Comentarios"
              >
                <i className="fas fa-comments"></i>
                {row.count_notes > 0 && (
                  <span
                    className="badge bg-danger rounded-circle position-absolute top-0 start-100 translate-middle"
                    style={{ zIndex: 1, fontSize: "10px", padding: "4px 6px" }}
                  >
                    {row.count_notes}
                  </span>
                )}
              </CitoPlusLinkIconPrimary>
            </ButtonGroup>
          );
        },
      },
    ],
    [],
  );

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedfetchUrl = useCallback(
    debounce((pageIndex, search) => {
      fetchUrl(pageIndex, search);
    }, 500),
    [],
  );

  useEffect(() => {
    fetchUrl(1);
  }, []);

  const fetchUrl = async (pageIndex = 1, search = "", ordering = "") => {
    setLoading(true);
    try {
      const params = {
        ...defaultParams,
        page: pageIndex,
        search: search || searchValue,
        ordering: ordering,
      };

      const response = await ResidenteService.getAutorizaciones(params);

      setNextUrl(response.data.next);
      setPreviousUrl(response.data.previous);
      setTotalPages(response.data.total_pages || 1);
      setCurrentPage(response.data.current_page || pageIndex);
      setData(response.data.results || []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setLoading(true);
    debouncedfetchUrl(1, value);
  };

  const handleEnterPress = () => {
    fetchUrl(1, searchValue);
  };

  const handleClearClick = () => {
    setSearchValue("");
    fetchUrl(1, "");
  };

  const handleSortingChange = (column) => {
    const { id } = column;
    const newOrder =
      sortField === id ? (order === "asc" ? "desc" : "asc") : "asc";

    setSortField(id);
    setOrder(newOrder);
    setLoading(true);
    fetchUrl(1, searchValue, `${newOrder === "desc" ? "-" : ""}${id}`);
  };

  const tableInstance = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
    },
    useFilters,
    usePagination,
  );

  const { page } = tableInstance;

  return (
    <>
      <div className="d-flex align-items-center mb-4 flex-wrap">
        <h4 className="fs-20 font-w600 me-auto">Mis Autorizaciones</h4>
        <LinkPrimary to="/autorizacion-crear">
          <i className="fas fa-plus me-2"></i>
          Nueva Autorización
        </LinkPrimary>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-tools">
            <SearchInput
              value={searchValue}
              onSearchChange={handleSearchChange}
              onEnterPress={handleEnterPress}
              onClearClick={handleClearClick}
            />
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <SpinnerCitoPlus text="Cargando autorizaciones..." />
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-file-signature fa-3x text-muted mb-3"></i>
              <h5>No hay autorizaciones</h5>
              <p className="text-muted">
                Crea una autorización para solicitar permisos especiales.
              </p>
              <LinkPrimary to="/autorizacion-crear" className="mt-2">
                <i className="fas fa-plus me-2"></i>
                Crear Autorización
              </LinkPrimary>
            </div>
          ) : (
            <Tables
              columns={columns}
              data={data}
              loading={loading}
              totalPages={totalPages}
              currentPage={currentPage}
              fetchUrl={fetchUrl}
              handleSortingChange={handleSortingChange}
              nextUrl={nextUrl}
              previousUrl={previousUrl}
              sortField={sortField}
              order={order}
              page={page}
            />
          )}
        </div>
      </div>

      <Comentarios
        showSidebarComments={showSidebarComments}
        setShowSidebarComments={setShowSidebarComments}
        urlComentarios={urlComentarios}
        urlComentariosParams={urlComentariosParams}
        urlPost={urlComentarios}
        selectedPrePostData={selectedPrePostData}
        handleNewComment={handleNewComment}
      />
    </>
  );
};

export default AutorizacionesList;
